import { useNavigate } from 'react-router-dom';
import { Payout, PayoutConnection } from '@shopify-multiseller/graphql/partner/types';
import { GET_PAYOUTS_QUERY } from './queries';
import { AdminUI } from '../../ui/AdminUI';
import { CommandHeader } from '../../components/CommandHeader';
import { SaveBarBlocker } from '../../context/SaveBarBlocker';
import { Badge } from '../../components/primitives';
import { DataList, type DataListColumn } from '../../components/DataList';
import { formatCents, formatDate } from '../../helpers/formatters';
import { useListView } from '../../hooks/useListView';

type PayoutsData = { payoutConnection: PayoutConnection };

const selectPayoutConnection = (data?: PayoutsData) => data?.payoutConnection;

const buildPayoutVariables = ({
  first,
  filter,
  after,
  before,
}: {
  first: number;
  filter: string;
  after?: string;
  before?: string;
}) => ({ first, filter, after, before });

/** Pull the settled amount + currency out of the transaction union. */
const payoutAmount = (payout: Payout): { amount?: number; currencyCode?: string } => {
  const transaction = payout.transaction;
  if (
    (transaction.__typename === 'PayoutTransactionStripe' ||
      transaction.__typename === 'PayoutTransactionManual' ||
      transaction.__typename === 'PayoutTransactionPaypal') &&
    transaction.to
  ) {
    return { amount: transaction.to.amount, currencyCode: transaction.to.currencyCode };
  }
  return {};
};

const columns: DataListColumn<Payout>[] = [
  {
    header: 'Payout',
    className: 'w-[38%]',
    cell: (payout) => <span className="font-semibold text-foreground">#{payout.idAlias}</span>,
  },
  {
    header: 'Paid',
    // Sellers reconcile payouts by day, not by minute — the time of day was
    // making this the widest column on the screen to no purpose.
    cell: (payout) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDate(payout.payoutDate, { year: 'numeric', month: 'short', day: 'numeric' })}
      </span>
    ),
  },
  {
    header: 'Status',
    cell: () => <Badge variant="success">Paid</Badge>,
  },
  {
    header: 'Amount',
    align: 'right',
    cell: (payout) => {
      const { amount, currencyCode } = payoutAmount(payout);
      if (amount === undefined) return <span className="text-muted-foreground">—</span>;
      return (
        <span className="tabular-nums text-foreground">
          {formatCents({ amount, currencyCode })}
        </span>
      );
    },
  },
];

export const Payouts: React.FC = () => {
  const navigate = useNavigate();
  const list = useListView<PayoutsData, Payout, null>({
    query: GET_PAYOUTS_QUERY,
    selectConnection: selectPayoutConnection,
    buildVariables: buildPayoutVariables,
    initialFilters: null,
    pageSize: 5,
  });

  return (
    <AdminUI>
      <SaveBarBlocker>
        <div className="min-h-screen">
          <CommandHeader title="Payouts" />

          <div className="mx-auto max-w-7xl px-6 py-5">
            <DataList
              variant="open"
              testId="payouts-list"
              list={list}
              columns={columns}
              getRowId={(payout) => payout.id}
              onRowClick={(payout) => navigate(`/payouts/${payout.id}`)}
              searchPlaceholder="Search payout number…"
              emptyTitle="No payouts yet"
              emptyDescription="Payouts collect your fulfilled orders and settle on the shop's payout schedule."
            />
          </div>
        </div>
      </SaveBarBlocker>
    </AdminUI>
  );
};
